/**
 * KolHomePage — קוֹל Home ("Forest"). Information-first and calm: a serif
 * greeting + compact car identity mark, the car's whole state in one honest
 * line (live with OBD, document-based without), a single focal countdown to the
 * soonest real renewal shown as a linear timeline (never a misleading dial,
 * never fabricated), and two quiet doors. Conversation lives in the dock.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  listVehicles,
  listDocumentsForVehicle,
  getVehicleEnrichment,
  getLatestObdScan,
  setDefaultVehicle,
  getVehicleFacts,
  logFactEvent,
  type VehicleSummary,
  type DocumentSummary,
  type SurfacedFact,
} from '@/lib/api';
import type { ObdScanResult } from '@tendedloop/shared';
import { useAuthStore } from '@/stores/auth.store';
import { VoiceDock } from '@/components/kol/VoiceDock';
import { Icon } from '@/components/kol/Icon';
import { CarMark } from '@/components/kol/CarMark';
import { InstallPrompt } from '@/components/kol/InstallPrompt';
import { AccountSheet } from '@/components/kol/AccountSheet';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { docTypeLabel, formatDocDate } from '@/lib/kol/docs';

function carLabel(v: VehicleSummary): string {
  if (v.customLabel?.trim()) return v.customLabel;
  const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  return `${cap(v.oem)} ${cap(v.model)}`;
}

interface Reminder {
  days: number;
  dateStr: string;
  label: string;
  /** true once the date has passed — surfaced as an alarm, not a countdown. */
  expired: boolean;
  /** the document this reminder is about, so the CTA opens it directly. */
  docId?: string;
}

/** Soonest upcoming expiry across registry validity + document expiries. */
function computeReminder(
  docs: DocumentSummary[],
  regValidUntil: string | null,
  t: (k: string, o?: Record<string, unknown>) => string,
): Reminder | null {
  const cands: Array<{ date: Date; label: string; docId?: string }> = [];
  if (regValidUntil) {
    const d = new Date(regValidUntil);
    if (!Number.isNaN(d.getTime())) {
      // Deep-link to the uploaded registration document if there is one.
      const regDoc = docs.find((x) => x.documentType === 'VEHICLE_REGISTRATION');
      cands.push({ date: d, label: t('kol.reg_label', { defaultValue: 'רישיון הרכב' }), docId: regDoc?.id });
    }
  }
  for (const doc of docs) {
    if (!doc.documentExpiresAt) continue;
    const d = new Date(doc.documentExpiresAt);
    if (!Number.isNaN(d.getTime())) cands.push({ date: d, label: docTypeLabel(doc.documentType, t), docId: doc.id });
  }
  const now = Date.now();
  const sorted = cands.slice().sort((a, b) => a.date.getTime() - b.date.getTime());
  // An already-expired item is the priority alarm (most overdue first); only if
  // nothing is expired do we fall back to the soonest upcoming renewal.
  const expiredItems = sorted.filter((c) => c.date.getTime() <= now);
  const upcoming = sorted.filter((c) => c.date.getTime() > now);
  const pick = expiredItems[0] ?? upcoming[0];
  if (!pick) return null;
  return {
    days: Math.ceil((pick.date.getTime() - now) / (1000 * 60 * 60 * 24)),
    dateStr: formatDocDate(pick.date.toISOString()) ?? '',
    label: pick.label,
    expired: pick.date.getTime() <= now,
    docId: pick.docId,
  };
}

function greeting(name: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const h = new Date().getHours();
  const key = h < 12 ? 'kol.greet_morning' : h < 18 ? 'kol.greet_afternoon' : 'kol.greet_evening';
  const dflt = h < 12 ? 'בוקר טוב' : h < 18 ? 'צהריים טובים' : 'ערב טוב';
  return t(key, { defaultValue: dflt }) + (name ? `, ${name}` : '');
}

export function KolHomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [vehicles, setVehicles] = useState<VehicleSummary[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reminder, setReminder] = useState<Reminder | null>(null);
  const [docCount, setDocCount] = useState<number | null>(null);
  const [scan, setScan] = useState<ObdScanResult | null>(null);
  const [facts, setFacts] = useState<SurfacedFact[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const install = useInstallPrompt();
  const [showInstall, setShowInstall] = useState(false);
  const [showAccount, setShowAccount] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listVehicles()
      .then((data) => {
        if (cancelled) return;
        setVehicles(data.items);
        setActiveId(data.items[0]?.id ?? null);
      })
      .catch(() => !cancelled && setError(t('common.error', { defaultValue: 'משהו השתבש' })));
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Active car's real facts: soonest renewal, doc count, latest OBD (for the
  // live state line). All best-effort; a missing source just trims the screen.
  useEffect(() => {
    if (!activeId) return;
    const vid = activeId;
    let cancelled = false;
    setReminder(null);
    setDocCount(null);
    setScan(null);
    setFacts([]);
    setDismissed(new Set());
    Promise.all([
      listDocumentsForVehicle(vid).catch(() => ({ items: [] as DocumentSummary[], total: 0 })),
      getVehicleEnrichment(vid).catch(() => null),
      getLatestObdScan(vid).catch(() => null),
      getVehicleFacts(vid).catch(() => ({ facts: [] as SurfacedFact[], asOf: '' })),
    ]).then(([docsRes, enr, obd, factsRes]) => {
      if (cancelled) return;
      setDocCount(docsRes.items.length);
      setReminder(computeReminder(docsRes.items, enr?.registrationValidUntil ?? null, t));
      setScan(obd);
      // The reminder card already owns test/insurance expiry; surface only the
      // new free-data facts here (recall + ambient chips), tiers above Ask-only.
      const visible = factsRes.facts.filter(
        (f) => ['recall', 'pollution', 'fuel_price'].includes(f.kind) && f.tier !== 'T3',
      );
      setFacts(visible);
      // Fire one impression per visible fact per Home load (best-effort).
      for (const f of visible) {
        void logFactEvent(vid, { kind: f.kind, key: f.key, event: 'impression', tier: f.tier });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activeId, t]);

  const dismissFact = (f: SurfacedFact) => {
    setDismissed((prev) => new Set(prev).add(f.key));
    if (activeId) void logFactEvent(activeId, { kind: f.kind, key: f.key, event: 'dismiss', tier: f.tier });
  };
  const tapFact = (f: SurfacedFact, url?: string) => {
    if (activeId) void logFactEvent(activeId, { kind: f.kind, key: f.key, event: 'tap', tier: f.tier });
    if (url) window.open(url.startsWith('http') ? url : `https://${url}`, '_blank', 'noopener,noreferrer');
  };
  const recallFacts = facts.filter((f) => f.kind === 'recall' && !dismissed.has(f.key));
  const chipFacts = facts.filter((f) => (f.kind === 'pollution' || f.kind === 'fuel_price') && !dismissed.has(f.key));

  useEffect(() => {
    if (!vehicles || vehicles.length === 0 || !install.eligible) return;
    let seen = false;
    try {
      seen = localStorage.getItem('kol_seen_home') === '1';
      localStorage.setItem('kol_seen_home', '1');
    } catch {
      /* ignore */
    }
    if (!seen) return;
    const timer = setTimeout(() => setShowInstall(true), 1400);
    return () => clearTimeout(timer);
  }, [vehicles, install.eligible]);

  // Prefer the user's chosen name (first word) over the email local-part.
  const name = user?.name?.trim().split(/\s+/)[0] || (user?.email ? (user.email.split('@')[0] ?? '') : '');
  const activeCar = vehicles?.find((v) => v.id === activeId) ?? null;
  const ask = () => activeId && navigate(`/kol/vehicles/${activeId}/ask`);
  // Set the household's main car from the garage chips. Optimistic: flip the
  // star immediately (no reorder mid-session, that would be jarring); the
  // backend list re-sorts main-first on the next load. Resync on failure.
  const makeMainCar = (id: string) => {
    setVehicles((prev) => prev?.map((v) => ({ ...v, isDefault: v.id === id })) ?? prev);
    void setDefaultVehicle(id).catch(() => {
      void listVehicles()
        .then((d) => setVehicles(d.items))
        .catch(() => undefined);
    });
  };
  // The reminder CTA opens the specific document it's about; only falls back to
  // the wallet when the reminder has no backing document (e.g. registry-only).
  const openReminder = () => {
    if (reminder?.docId) navigate(`/documents/${reminder.docId}?kol=1`);
    else navigate('/kol/documents');
  };
  const daysWord = (n: number) =>
    n === 1 ? t('kol.day', { defaultValue: 'יום' }) : t('kol.days', { defaultValue: 'ימים' });

  // ---- honest car-state line (live with OBD, documents without) ----
  const fuel = scan?.pids.find((p) => p.key === 'fuelLevel')?.value;
  const dtcCount = scan ? scan.storedDtcs.length : null;
  const hasIssue = dtcCount != null && dtcCount > 0;
  let stateDot = 'bg-kol-leaf-bright';
  let stateText: string;
  if (reminder?.expired) {
    // An expired document/registration outranks everything else — it needs action.
    stateDot = 'bg-kol-clay';
    stateText = t('kol.state_expired', { defaultValue: '{{label}} פג תוקף · לחדש בהקדם', label: reminder.label });
  } else if (scan && hasIssue) {
    stateDot = 'bg-kol-clay';
    stateText = t('kol.state_issue', { defaultValue: '{{n}} קודי תקלה · כדאי לבדוק', n: dtcCount });
  } else if (scan) {
    const fuelStr =
      typeof fuel === 'number' ? t('kol.state_fuel', { defaultValue: ' · {{f}}% דלק', f: Math.round(fuel) }) : '';
    stateText = t('kol.state_ok', { defaultValue: 'הכול תקין{{fuel}} · נסרק', fuel: fuelStr });
  } else {
    // No live scan connected — say nothing about OBD; reassure from documents.
    stateText = t('kol.state_docs', { defaultValue: 'המסמכים שלך בתוקף' });
  }

  // linear-countdown fill: position within an assumed 1-year term (the number
  // is the truth; the bar is an honest "how close" within an annual frame).
  const elapsed = reminder ? Math.max(0.04, Math.min(0.96, (365 - reminder.days) / 365)) : 0;

  return (
    <div className="kol-grain font-kol min-h-screen bg-kol-canvas text-kol-ink flex flex-col relative" dir={i18n.dir()}>
      <div className="relative z-[1] flex items-center justify-between px-[22px] pt-[max(20px,env(safe-area-inset-top))] pb-2">
        <div className="flex items-center gap-3 min-w-0">
          {vehicles && vehicles.length > 0 && <CarMark size={58} className="text-kol-pine shrink-0" />}
          <div className="min-w-0">
            <h1 className="font-kol-display text-[23px] font-medium leading-none">{greeting(name, t)}</h1>
            {activeCar && (
              <div className="text-[12.5px] text-kol-ink-soft font-semibold mt-1 truncate">
                {carLabel(activeCar)}
                {activeCar.licensePlate ? ` · ${activeCar.licensePlate}` : ''}
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          aria-label={t('kol.account.open', { defaultValue: 'החשבון שלי' })}
          onClick={() => setShowAccount(true)}
          className="w-11 h-11 rounded-xl border border-kol-line bg-kol-card grid place-items-center text-kol-ink-soft shrink-0"
        >
          <Icon name="sliders" size={20} />
        </button>
      </div>

      <div className="relative z-[1] flex-1 overflow-y-auto px-[22px] pb-2 flex flex-col gap-3.5">
        {error && (
          <div
            role="alert"
            className="rounded-[20px] bg-kol-clay-soft border border-[#ecd9c9] text-kol-clay-ink p-4 text-[15px]"
          >
            {error}
          </div>
        )}

        {vehicles === null && !error && (
          <div className="text-kol-ink-faint text-center pt-10">{t('common.loading', { defaultValue: 'טוען…' })}</div>
        )}

        {vehicles && vehicles.length === 0 && (
          <div className="pt-12 flex flex-col items-center gap-5 text-center">
            <CarMark size={120} className="text-kol-pine" />
            <div className="font-kol-display text-[22px] font-medium leading-snug">
              {t('kol.empty_title', { defaultValue: 'בואו נכיר את הרכב שלכם' })}
              <span className="block text-[15px] font-normal font-kol text-kol-ink-soft mt-2">
                {t('kol.empty_sub', { defaultValue: 'צלמו את רישיון הרכב, והוא יזהה את עצמו.' })}
              </span>
            </div>
            <button
              type="button"
              onClick={() => navigate('/upload/registration?kol=1')}
              className="rounded-[20px] bg-kol-pine text-white font-semibold px-7 py-3.5 text-[16px] shadow-[0_16px_30px_-18px_rgba(18,50,38,0.7)]"
            >
              {t('kol.empty_cta', { defaultValue: 'צילום רישיון רכב' })}
            </button>
          </div>
        )}

        {vehicles && vehicles.length > 0 && (
          <>
            {/* Garage row: switch between cars + add a new one. The add
                affordance shows whenever there's a car so it's never buried —
                a full-width pill when you have one car, an icon beside the
                switcher chips when you have several. */}
            <div className="flex gap-2.5">
              {vehicles.length > 1 &&
                vehicles.map((v) => {
                  const on = v.id === activeId;
                  const isMain = v.isDefault;
                  return (
                    <div key={v.id} className="relative flex-1">
                      <button
                        type="button"
                        onClick={() => setActiveId(v.id)}
                        className={[
                          'w-full rounded-[16px] ps-3.5 pe-11 py-2.5 text-start border min-h-[52px]',
                          on ? 'bg-kol-sage-soft border-kol-pine' : 'bg-kol-card border-kol-line-soft',
                        ].join(' ')}
                      >
                        <div className="flex items-center gap-2">
                          {on && <span className="w-2 h-2 rounded-full bg-kol-pine" />}
                          <span className="text-[14.5px] font-bold truncate">{carLabel(v)}</span>
                        </div>
                      </button>
                      {/* Star marks the main car (filled) and, on the others,
                          sets it as main in one tap. 44px hit area. */}
                      <button
                        type="button"
                        onClick={() => makeMainCar(v.id)}
                        disabled={isMain}
                        aria-label={
                          isMain
                            ? t('kol.is_main_car', { defaultValue: 'הרכב הראשי' })
                            : t('kol.set_main_car', { defaultValue: 'הגדרה כרכב ראשי' })
                        }
                        className="absolute inset-y-0 end-0 w-11 grid place-items-center disabled:opacity-100"
                      >
                        <Icon
                          name="star"
                          size={18}
                          {...(isMain ? { fill: 'currentColor' } : {})}
                          className={isMain ? 'text-kol-pine' : 'text-kol-ink-faint'}
                        />
                      </button>
                    </div>
                  );
                })}
            </div>

            {/* honest state line */}
            <div className="flex items-center gap-2.5 text-[14px] text-kol-ink-soft font-semibold px-1">
              <span className={`w-2 h-2 rounded-full ${stateDot}`} />
              <span>{stateText}</span>
            </div>

            {/* focal: an expired item alarms (clay); otherwise the soonest
                renewal as an honest linear countdown */}
            {reminder && reminder.expired ? (
              <button
                type="button"
                onClick={openReminder}
                className="text-start rounded-[24px] bg-kol-clay-soft border border-[#E6C6AB] shadow-kol-2 p-[22px]"
              >
                <div className="flex items-center gap-2 text-[12.5px] font-bold text-kol-clay-ink">
                  <Icon name="warning" size={15} />
                  {reminder.label} · {t('kol.expired_flag', { defaultValue: 'פג תוקף' })}
                </div>
                <div className="font-kol-display font-bold text-[28px] leading-tight text-kol-clay-ink mt-2">
                  {t('kol.expired_title', { defaultValue: 'צריך לחדש עכשיו' })}
                </div>
                <div className="text-[13.5px] text-kol-clay-ink/85 font-medium mt-1.5">
                  {t('kol.expired_meta', {
                    defaultValue: 'פג תוקף {{date}} · לפני {{n}} ימים',
                    date: reminder.dateStr,
                    n: Math.abs(reminder.days),
                  })}
                </div>
                <div className="flex items-center gap-2 text-kol-clay-ink font-bold text-[13.5px] mt-4">
                  {t('kol.open_policy', { defaultValue: 'פתחו את המסמך' })}
                  <Icon name="chevron" size={15} />
                </div>
              </button>
            ) : reminder ? (
              <button
                type="button"
                onClick={openReminder}
                className="text-start rounded-[24px] bg-kol-card border border-kol-line-soft shadow-kol-2 p-[22px]"
              >
                <div className="text-[12.5px] font-bold text-kol-leaf">
                  {t('kol.next_reminder', { defaultValue: 'התזכורת הקרובה' })} · {reminder.label}
                </div>
                <div className="flex items-baseline gap-1.5 mt-2">
                  <span className="font-kol-display font-bold text-[48px] leading-none text-kol-pine tabular-nums">
                    {reminder.days}
                  </span>
                  <span className="text-[18px] font-bold text-kol-leaf">{daysWord(reminder.days)}</span>
                </div>
                <div className="text-[13.5px] text-kol-ink-soft font-medium mt-2">
                  {t('kol.reminder_meta2', {
                    defaultValue: 'מסתיים {{date}} · אזכיר לכם לפני',
                    date: reminder.dateStr,
                  })}
                </div>
                <div className="h-2.5 rounded-full bg-kol-sage-soft mt-[18px] relative">
                  <div
                    className="absolute inset-y-0 start-0 rounded-full"
                    style={{ width: `${elapsed * 100}%`, background: 'linear-gradient(90deg, #3CA06F, #1F4E3D)' }}
                  />
                  <div
                    className="absolute -top-[5px] w-5 h-5 rounded-full bg-white border-[3px] border-kol-pine"
                    style={{ insetInlineStart: `${elapsed * 100}%`, transform: 'translateX(50%)' }}
                  />
                </div>
                <div className="flex justify-between text-[11.5px] text-kol-ink-faint font-semibold mt-2.5">
                  <span>{t('kol.coverage_start', { defaultValue: 'תחילת הכיסוי' })}</span>
                  <span>{reminder.dateStr}</span>
                </div>
                <div className="flex items-center gap-2 text-kol-pine font-bold text-[13.5px] mt-4">
                  {t('kol.open_policy', { defaultValue: 'פתחו את המסמך' })}
                  <Icon name="chevron" size={15} />
                </div>
              </button>
            ) : (
              <div className="rounded-[24px] bg-kol-card border border-kol-line-soft shadow-kol-2 p-5 flex items-center gap-3.5">
                <span className="w-11 h-11 rounded-xl bg-kol-sage-soft text-kol-pine grid place-items-center flex-none">
                  <Icon name="check" size={22} />
                </span>
                <div>
                  <div className="font-kol-display text-[18px] font-medium">
                    {t('kol.all_clear2', { defaultValue: 'הכול מסודר' })}
                  </div>
                  <div className="text-[13.5px] text-kol-ink-soft font-medium mt-0.5">
                    {t('kol.all_clear_sub', { defaultValue: 'אין תזכורות קרובות.' })}
                  </div>
                </div>
              </div>
            )}

            {/* Free-data facts: open recalls (cards) + ambient chips. The
                reminder card above owns test/insurance expiry. */}
            {recallFacts.map((f) => {
              const safety = f.severity === 'safety';
              const reason = typeof f.params.reason === 'string' ? f.params.reason : '';
              const detail = typeof f.params.detail === 'string' ? f.params.detail : '';
              const website = typeof f.params.website === 'string' ? f.params.website : '';
              return (
                <div
                  key={f.key}
                  className={`relative text-start rounded-[24px] border shadow-kol-2 p-[22px] ${
                    safety ? 'bg-kol-clay-soft border-[#E6C6AB]' : 'bg-kol-card border-kol-line-soft'
                  }`}
                >
                  <button
                    type="button"
                    aria-label={t('common.dismiss', { defaultValue: 'התעלם' })}
                    onClick={() => dismissFact(f)}
                    className="absolute top-3 end-3 w-9 h-9 grid place-items-center text-kol-ink-faint"
                  >
                    <Icon name="close" size={16} />
                  </button>
                  <div
                    className={`flex items-center gap-2 text-[12.5px] font-bold ${
                      safety ? 'text-kol-clay-ink' : 'text-kol-leaf'
                    }`}
                  >
                    <Icon name="warning" size={15} />
                    {t('kol.facts.recall_title', { defaultValue: 'קריאת בטיחות (RECALL)' })}
                  </div>
                  {reason && (
                    <div className="font-kol-display font-medium text-[19px] leading-snug mt-2 pe-8">{reason}</div>
                  )}
                  {detail && <div className="text-[13.5px] text-kol-ink-soft font-medium mt-1.5">{detail}</div>}
                  {website && (
                    <button
                      type="button"
                      onClick={() => tapFact(f, website)}
                      className={`flex items-center gap-2 font-bold text-[13.5px] mt-4 ${
                        safety ? 'text-kol-clay-ink' : 'text-kol-pine'
                      }`}
                    >
                      {t('kol.facts.recall_cta', { defaultValue: 'פרטים אצל היבואן' })}
                      <Icon name="chevron" size={15} />
                    </button>
                  )}
                </div>
              );
            })}

            {chipFacts.length > 0 && (
              <div>
                <div className="text-[13px] font-semibold text-kol-ink-faint mb-1.5">
                  {t('kol.facts.ambient_label', { defaultValue: 'נתון אגבי' })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {chipFacts.map((f) => (
                    <span
                      key={f.key}
                      className="inline-flex items-center gap-2 rounded-full bg-kol-card border border-kol-line-soft ps-3.5 pe-2 py-1.5 text-[15px] font-semibold text-kol-ink-soft"
                    >
                      {f.kind === 'pollution' ? (
                        t('kol.facts.pollution_chip', {
                          defaultValue: 'דרגת זיהום {{group}}',
                          group: Number(f.params.pollutionGroup),
                        })
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          {t('kol.facts.fuel_chip_label', { defaultValue: 'בנזין 95' })}
                          {/* Isolate the numeric price (LTR) from the unit so the
                              ₪ glyph next to digits doesn't visually collapse to ש. */}
                          <bdi dir="ltr">{Number(f.params.price95)}</bdi>
                          <span>{t('kol.facts.fuel_chip_unit', { defaultValue: '₪ / ל׳' })}</span>
                        </span>
                      )}
                      <button
                        type="button"
                        aria-label={t('common.dismiss', { defaultValue: 'התעלם' })}
                        onClick={() => dismissFact(f)}
                        className="text-kol-ink-faint"
                      >
                        <Icon name="close" size={13} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* two quiet doors */}
            <div className="flex gap-3">
              {scan ? (
                <button
                  type="button"
                  onClick={() => activeId && navigate(`/kol/vehicles/${activeId}`)}
                  className="flex-1 text-start rounded-[18px] bg-kol-card border border-kol-line-soft p-4 flex items-center gap-3"
                >
                  <span className="w-10 h-10 rounded-xl bg-kol-sage-soft text-kol-pine grid place-items-center flex-none">
                    <Icon name="activity" size={20} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[14.5px] font-bold">{t('kol.car_state', { defaultValue: 'מצב הרכב' })}</div>
                    <div className="text-[15px] text-kol-ink-faint font-semibold mt-0.5 truncate">
                      {typeof fuel === 'number' ? `${Math.round(fuel)}% דלק · ` : ''}
                      {hasIssue
                        ? t('kol.engine_n', { defaultValue: '{{n}} תקלות', n: dtcCount })
                        : t('kol.engine_ok', { defaultValue: 'מנוע תקין' })}
                    </div>
                  </div>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => activeId && navigate(`/kol/vehicles/${activeId}`)}
                  className="flex-1 text-start rounded-[18px] bg-kol-card border border-kol-line-soft p-4 flex items-center gap-3"
                >
                  <span className="w-10 h-10 rounded-xl bg-kol-sage-soft text-kol-pine grid place-items-center flex-none">
                    <Icon name="compass" size={20} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[14.5px] font-bold">
                      {t('kol.vehicle_details', { defaultValue: 'פרטי הרכב' })}
                    </div>
                    <div className="text-[15px] text-kol-ink-faint font-semibold mt-0.5 truncate">
                      {t('kol.vehicle_details_sub', { defaultValue: 'זהות, מפרט ותאריכים' })}
                    </div>
                  </div>
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/kol/documents')}
                className="flex-1 text-start rounded-[18px] bg-kol-card border border-kol-line-soft p-4 flex items-center gap-3"
              >
                <span className="w-10 h-10 rounded-xl bg-kol-sage-soft text-kol-pine grid place-items-center flex-none">
                  <Icon name="folder" size={20} />
                </span>
                <div className="min-w-0">
                  <div className="text-[14.5px] font-bold">{t('kol.documents', { defaultValue: 'המסמכים' })}</div>
                  <div className="text-[15px] text-kol-ink-faint font-semibold mt-0.5">
                    {t('kol.docs_n', { defaultValue: '{{n}} שמורים', n: docCount ?? 0 })}
                  </div>
                </div>
              </button>
            </div>

            {/* One primary, context-free action: add a document. A vehicle
                registration auto-creates the car; everything else auto-binds. */}
            <button
              type="button"
              onClick={() => navigate('/upload/registration?kol=1')}
              className="w-full rounded-[20px] bg-kol-pine text-white font-semibold py-4 text-[16.5px] min-h-[54px] shadow-[0_16px_30px_-18px_rgba(31,80,66,0.75)] flex items-center justify-center gap-2"
            >
              <Icon name="plus" size={20} /> {t('kol.add_document', { defaultValue: 'הוספת מסמך' })}
            </button>
          </>
        )}
      </div>

      {vehicles && vehicles.length > 0 && (
        <div className="relative z-[1] px-[22px] pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
          <VoiceDock
            placeholder={t('kol.dock2', { defaultValue: 'שאלו את הרכב או דברו אליו…' })}
            onActivate={ask}
            showOrb={false}
          />
        </div>
      )}

      {showAccount && <AccountSheet onClose={() => setShowAccount(false)} />}

      {showInstall && (
        <InstallPrompt
          isIos={install.isIos}
          canPrompt={install.canPrompt}
          onInstall={async () => {
            await install.promptInstall();
            setShowInstall(false);
          }}
          onDismiss={() => {
            install.dismiss();
            setShowInstall(false);
          }}
        />
      )}
    </div>
  );
}
